import {
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useBoolean,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import { useMarkTaskNotificationsAsUnreadMutation } from "@src/__generated__/graphql";
import {
  TaskSystemStatusEnum,
  useMarkTaskNotificationsAsReadMutation,
} from "@src/__generated__/urql-graphql";
import type {
  TaskNotification,
  TaskNotificationScoped,
} from "@src/components/modules/notifications/listing/store";
import type { InternalTaskType } from "@src/components/modules/resource-planning/tasks/listing/store";
import { onError } from "@src/utils/apollo";
import { useStore } from "@src/utils/hooks";
import { kebabCase } from "lodash";
import { observer } from "mobx-react-lite";
import { type FC, useEffect } from "react";
import { RoundToggleButton } from "../Button";
import { Icon } from "../Icon";
import { MenuItemLink } from "../Link";
import { TaskItemBase, type TaskItemBaseProps } from "./TaskItemBase";

type TTaskItemNotificationProps = Omit<
  TaskItemBaseProps<InternalTaskType>,
  "content" | "title" | "task"
> & {
  hasUnreadNotifications: boolean;
  notificationsCount: number;
  task: TaskNotification["task"] | TaskNotificationScoped["task"];
};

export const TaskItemNotification: FC<TTaskItemNotificationProps> = observer(
  function TaskItemNotification(props) {
    const { notificationsStore: store } = useStore();
    const [isRead, setIsRead] = useBoolean(props.hasUnreadNotifications);
    const isTaskDone =
      props.task?.status.system_status === TaskSystemStatusEnum.Done;

    const [markNotificationsAsReadResult, markNotificationsAsReadOperation] =
      useMarkTaskNotificationsAsReadMutation();
    const loadingMarkAsRead = markNotificationsAsReadResult.fetching;
    const markNotificationsAsRead = async () => {
      const result = await markNotificationsAsReadOperation({
        task_id: props.task?.id ?? "",
      });
      if (result.data?.markTaskAndCommunicationNotificationAsRead) {
        if (!props.task?.id) return;
        store.toggleTaskNotificationRead(props.task.id);
        store.decrementUnreadTaskNotificationsBy(props.notificationsCount);
      }
    };

    const [markNotificationsAsUnread, { loading: loadingMarkAsUnread }] =
      useMarkTaskNotificationsAsUnreadMutation({
        variables: {
          task_id: props.task?.id ?? "",
        },
        onCompleted(data) {
          if (data.markTaskAndCommunicationNotificationAsUnread) {
            if (!props.task?.id) return;
            store.toggleTaskNotificationRead(props.task.id);
            store.incrementUnreadTaskNotificationsBy(props.notificationsCount);
          }
        },
        ...onError(),
      });

    useEffect(() => {
      if (props.hasUnreadNotifications) {
        setIsRead.off();
      } else {
        setIsRead.on();
      }
    }, [props.hasUnreadNotifications]);

    return (
      <TaskItemBase
        isSubtask
        title={
          "stats" in props.task
            ? `${props.task?.ourWorkBudgetItem?.project.code} - ${props.task?.ourWorkBudgetItem?.title}`
            : `${props.task.ourWorkBudgetItem?.project.code} - ${props.task.name}`
        }
        wrapperStyle={{
          color: isTaskDone ? "gray.500" : undefined,
          bg: isTaskDone ? "grey.50" : isRead ? "#f4f6f8" : "white",
          gridTemplateColumns: "auto auto 1fr auto auto auto",
        }}
        content={(task) => {
          return (
            <Menu>
              <MenuButton
                as={IconButton}
                onClick={(e) => e.stopPropagation()}
                size="sm"
                variant="ghost"
              >
                <Icon name="dots-horizontal" w="5" h="5" />
              </MenuButton>
              <MenuList onClick={(e) => e.stopPropagation()}>
                <MenuItem
                  onClick={() => {
                    markNotificationsAsRead();
                    props.onClick?.(task);
                  }}
                >{t`Open`}</MenuItem>
                {task.ourWorkBudgetItem && (
                  <MenuItemLink
                    href={{
                      pathname: "/projects/detail",
                      query: { id: task.ourWorkBudgetItem.project.id },
                      hash: kebabCase(task.ourWorkBudgetItem.title),
                    }}
                  >
                    <Trans>Go to project overview</Trans>
                  </MenuItemLink>
                )}
                <MenuItem
                  onClick={() => {
                    isRead
                      ? markNotificationsAsUnread()
                      : markNotificationsAsRead();
                  }}
                >
                  {isRead ? t`Mark as unread` : t`Mark as read`}
                </MenuItem>
              </MenuList>
            </Menu>
          );
        }}
        extraContent={() => {
          return (
            <RoundToggleButton
              pl="0"
              pr="2"
              loading={loadingMarkAsRead || loadingMarkAsUnread}
              isActive={isRead}
              onToggle={() =>
                isRead ? markNotificationsAsUnread() : markNotificationsAsRead()
              }
            />
          );
        }}
        {...props}
        task={
          "stats" in props.task
            ? props.task
            : {
                ...props.task,
                billable: true,
                scopes: [],
                commentCount: 0,
                unreadCommentsCount: 0,
                scopedCommentCount: 0,
                ourWorkBudgetItem: {
                  id: props.task.ourWorkBudgetItem?.id ?? "",
                  title: "",
                  project: {
                    id: props.task.ourWorkBudgetItem?.project.id ?? "",
                    code: props.task.ourWorkBudgetItem?.project.code ?? "",
                    uses_budgetovac:
                      props.task.ourWorkBudgetItem?.project.uses_budgetovac ??
                      false,
                    title: props.task.ourWorkBudgetItem?.project.title ?? "",
                    brand: {
                      name:
                        props.task.ourWorkBudgetItem?.project.brand.name ?? "",
                      client: props.task.ourWorkBudgetItem?.project.brand
                        .client ?? {
                        name: "",
                      },
                    },
                  },
                },
              }
        }
        onClick={(task) => {
          markNotificationsAsRead();
          props.onClick?.(task);
        }}
      />
    );
  },
);
